import { useCallback, useMemo, useState } from "react"
import { Box, Flex, Grid, Text, VStack } from "@chakra-ui/react"
import type { Graph } from "@hydrologiq/schemas/1.0.0/installationPage"
import type { GraphRequest } from "@hydrologiq/gql-experience/graphql"
import type { GetAssetQuery } from "@hydrologiq/gql-experience/graphql"
import {
  useMonitoringGraphs,
  FIXED_MONITORING_GRAPHS,
  filterGraphsByAvailableClasses,
  TimescaleTimeSeriesGraph,
  type TimescaleGraphTemplate,
  type TimescaleMonitoringGraphPayload,
} from "@hydrologiq/shared/monitoring"
import {
  buildLinearTimeAxisTicksMs,
  calculateDynamicAxisInterval,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"
import {
  boundarySourceFromAssignments,
  type TimescalePeriodValue,
} from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRange"
import { useTimeRangeController } from "@hydrologiq/shared/monitoring/builders/2.0.0/useTimeRangeController"
import { useAutoRefresh } from "@hydrologiq/shared/monitoring/builders/2.0.0/useAutoRefresh"
import { mapRangeGranularity } from "@hydrologiq/shared/monitoring/builders/2.0.0/RangeGranularity"
import { TimeRangeControl } from "@hydrologiq/shared/monitoring/builders/2.0.0/TimeRangeControl"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import { useGetPref, useSetPref } from "@hydrologiq/shared/providers/helpers/usePref"
import AssetHeadlineFigures from "../asset/AssetHeadlineFigures"

type Asset = GetAssetQuery["asset"]

// Templates that are superseded on the hybrid parent view. "Fuel Level"
// (diesel-only, m³) is replaced here by the per-category "Tank Level" card
// (diesel/water/waste, %), so it is hidden on the hybrid parent only — the
// system view keeps it. Module-scoped constant so the filter memo below has
// honest deps and no per-render allocation.
const HYBRID_PARENT_HIDDEN_TITLES = new Set(["Fuel Level"])

export interface HybridParentMonitoringProps {
  asset: Asset
}

/**
 * Compute a stable graph ID from a template definition.
 */
function getGraphId(template: TimescaleGraphTemplate): string {
  const field = template.source?.field ?? ""
  if (template.filter?.role) return `${field}__role_${template.filter.role}`
  if (template.filter?.class) return `${field}__class_${template.filter.class}`
  return field
}

/**
 * True if a batch payload has at least one finite numeric sample.
 */
function batchPayloadHasNumericData(
  payload: TimescaleMonitoringGraphPayload | undefined
): boolean {
  if (!payload) return false
  const hasNumericRow = (data: Array<Record<string, unknown>>) =>
    data.some((row) =>
      Object.entries(row).some(
        ([key, v]) => key !== "timestamp" && typeof v === "number" && Number.isFinite(v)
      )
    )
  if (payload.aggregated?.data?.length && hasNumericRow(payload.aggregated.data)) return true
  if (payload.breakdown.data.length && hasNumericRow(payload.breakdown.data)) return true
  return false
}

/**
 * System-style monitoring view for hybrid parent assets.
 *
 * Uses the same shared TimescaleTimeSeriesGraph component as the system
 * monitoring view, ensuring identical rendering (stacked bars, overlays,
 * peaks, battery charts, etc.) with no duplicated logic.
 */
export default function HybridParentMonitoring({ asset }: HybridParentMonitoringProps) {
  const getPref = useGetPref()
  const setPref = useSetPref()

  // Derive available child classes from the asset's children
  const availableAssetClasses = useMemo(() => {
    if (!asset.children?.length) return undefined
    const classes = new Set<string>()
    for (const child of asset.children) {
      if (child.class) classes.add(child.class.toLowerCase())
    }
    return classes.size > 0 ? Array.from(classes) : undefined
  }, [asset.children])

  // Filter graph templates by available child classes, then drop templates that
  // are superseded on the hybrid parent view (see HYBRID_PARENT_HIDDEN_TITLES).
  const templates = useMemo(() => {
    return filterGraphsByAvailableClasses(FIXED_MONITORING_GRAPHS, availableAssetClasses).filter(
      (t) => !HYBRID_PARENT_HIDDEN_TITLES.has(t.title ?? "")
    )
  }, [availableAssetClasses])

  // Time range: bounds come from the monitored assignments; the browser timezone
  // is resolved once here and passed down.
  const browserTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])
  const boundarySource = useMemo(
    () =>
      boundarySourceFromAssignments(
        asset.assignments?.range ?? asset.assignments?.all ?? [],
        asset.assignments?.monitored
      ),
    [asset]
  )
  const assignmentEndDate = boundarySource.activity === "ended" ? boundarySource.endedAt : undefined

  const savedPeriod = getPref("timescalePeriod")
  const handleRelativePresetApplied = useCallback(
    (preset: TimescalePeriodValue) => setPref("timescalePeriod", preset),
    [setPref]
  )
  const { applied, apply, refresh, refreshKey } = useTimeRangeController({
    boundarySource,
    browserTimeZone,
    persistedPreset: savedPeriod,
    onRelativePresetApplied: handleRelativePresetApplied,
  })
  const startDate = applied.startUtc
  const endDate = applied.endUtc
  const autoRefresh = useAutoRefresh({ apply, refresh, enabled: applied.mode === "relative" })

  const rangeTimescale = useMemo(
    () => mapRangeGranularity(startDate, endDate),
    [startDate, endDate]
  )

  // Build graph requests from the filtered templates (same logic as system view)
  const graphRequests: GraphRequest[] = useMemo(() => {
    const requests: GraphRequest[] = []

    for (const template of templates) {
      const graphId = getGraphId(template)
      const field = template.source?.field ?? ""
      const filter = template.filter
      const hasAgg = template.aggregation === "system"
      const dual = template.dualMetric === true
      const isBatteryView = template.batteryView === true
      const includeConnections =
        template.includeConnections === true ||
        template.connectionsOnly === true ||
        (template as any).aggregationRole === "load" ||
        (filter?.role === "consumer" && field === "power")
      const connectionsOnly = template.connectionsOnly === true

      const overlayGraphIds = template.availableOverlays?.length
        ? template.availableOverlays.map((o) => o.graphId)
        : null

      if (isBatteryView) {
        requests.push({
          graphId: `${graphId}_discharge`,
          field: "power",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
          connectionsOnly: true,
        })
        requests.push({
          graphId: `${graphId}_charge`,
          field: "upstream_power",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
          connectionsOnly: true,
        })
        requests.push({
          graphId: `${graphId}_soc`,
          field: "soc",
          role: null,
          assetClass: filter?.class ?? null,
          includeAggregated: false,
          includeBreakdown: true,
          secondaryField: null,
        })
      } else {
        const isLoadGraph = filter?.role === "consumer" && (template as any).aggregationRole === "load"
        const req: GraphRequest = {
          graphId,
          field,
          role: filter?.role ?? null,
          assetClass: filter?.class ?? null,
          includeAggregated: hasAgg,
          includeBreakdown: true,
          // The fuel series is signed in the database: a bucket holds the fall
          // in tank level since the previous one, so movement within the
          // sender's tolerance leaves small negatives. `fuel_consumed_displayed`
          // is the same series with those settled into their neighbours, which
          // is what a chart can draw. The signed measurement is still exported
          // in the CSV for analysis.
          secondaryField: dual ? "fuel_consumed_displayed" : null,
          includeConnections,
          connectionsOnly,
          ...((template as any).aggregationRole ? { aggregationRole: (template as any).aggregationRole } : {}),
          ...(overlayGraphIds ? { overlayGraphIds } : {}),
          ...(isLoadGraph ? { includePhaseBalance: true } : {}),
        }
        requests.push(req)
      }
    }

    return requests
  }, [templates])

  // Memoize scope and date strings to prevent infinite re-render loops
  const scope = useMemo(
    () => ({ type: "parentAsset" as const, parentAssetId: asset.id }),
    [asset.id]
  )
  const startIso = useMemo(() => startDate.toIsoString(), [startDate])
  const stopIso = useMemo(() => endDate.toIsoString(), [endDate])

  // Fetch graphs using the shared hook with parent asset scope
  const { graphsData, loading } = useMonitoringGraphs({
    scope,
    start: startIso,
    stop: stopIso,
    granularity: rangeTimescale.granularity,
    refreshKey,
    graphs: graphRequests,
  })

  // Overlay toggle state: graphId -> Set of active overlay graphIds
  const [graphOverlayState, setGraphOverlayState] = useState<Map<string, Set<string>>>(() => {
    const initialState = new Map<string, Set<string>>()
    for (const template of templates) {
      const graphId = getGraphId(template)
      const defaults = template.availableOverlays
        ?.filter((o) => o.defaultEnabled)
        .map((o) => o.graphId)
      if (defaults?.length) {
        initialState.set(graphId, new Set(defaults))
      }
    }
    return initialState
  })

  // Shared timestamps for consistent x-axis alignment
  const sharedTimestamps = useMemo(() => {
    if (loading || graphsData.size === 0) return undefined
    const timestampSet = new Set<string>()
    for (const payload of graphsData.values()) {
      if (payload.breakdown?.data) {
        for (const point of payload.breakdown.data) {
          if (point.timestamp) timestampSet.add(point.timestamp as string)
        }
      }
      if (payload.aggregated?.data) {
        for (const point of payload.aggregated.data) {
          if (point.timestamp) timestampSet.add(point.timestamp as string)
        }
      }
    }
    return Array.from(timestampSet).sort()
  }, [graphsData, loading])

  const sharedXAxisInterval = useMemo(() => {
    if (!sharedTimestamps?.length) return undefined
    return calculateDynamicAxisInterval(startDate, endDate, sharedTimestamps.length)
  }, [startDate, endDate, sharedTimestamps])

  const sharedXAxisTicksMs = useMemo(
    () => buildLinearTimeAxisTicksMs(startDate, endDate, 8),
    [startDate, endDate]
  )

  // Build renderable graph entries: templates that have data
  const renderableGraphs = useMemo(() => {
    if (loading) return templates
    return templates.filter((template) => {
      const graphId = getGraphId(template)
      if (template.batteryView) {
        return (
          batchPayloadHasNumericData(graphsData.get(`${graphId}_discharge`)) ||
          batchPayloadHasNumericData(graphsData.get(`${graphId}_charge`)) ||
          batchPayloadHasNumericData(graphsData.get(`${graphId}_soc`))
        )
      }
      return batchPayloadHasNumericData(graphsData.get(graphId))
    })
  }, [templates, graphsData, loading])

  // Determine if there's any data at all
  const hasAnyData = useMemo(() => {
    if (graphsData.size === 0) return false
    return Array.from(graphsData.values()).some(batchPayloadHasNumericData)
  }, [graphsData])

  return (
    <SectionRoot>
      <Section separator={false}>
        <Flex direction="column" gap={0} p={0}>
          <AssetHeadlineFigures
            asset={asset}
            stop={assignmentEndDate}
            mode="monitoring"
            refreshVersion={refreshKey}
          />

          <Box mt={{ base: 2, md: 4 }} width="100%">
            <TimeRangeControl
              applied={applied}
              boundarySource={boundarySource}
              browserTimeZone={browserTimeZone}
              onApply={autoRefresh.apply}
              onOpenChange={autoRefresh.onOpenChange}
            />
          </Box>

          <Grid
            templateColumns={{ base: "1fr", lg: "1fr" }}
            gap={{ base: 6, md: 8 }}
            mt={{ base: 6, md: 8 }}
            width="100%"
            minW={0}
          >
            {!loading && graphsData.size > 0 && !hasAnyData ? (
              <Box
                gridColumn="1 / -1"
                bg="white"
                borderRadius="5px"
                boxShadow="card"
                p={{ base: 8, md: 12 }}
                textAlign="center"
              >
                <VStack gap={3}>
                  <Text fontSize="lg" fontWeight="medium" color="fg.muted">
                    Waiting for data
                  </Text>
                  <Text fontSize="sm" color="fg.subtle" maxW="420px">
                    If you have recently connected a data source, data should be available within 10 minutes. Relative time ranges update automatically every five minutes.
                  </Text>
                </VStack>
              </Box>
            ) : (
              renderableGraphs.map((template) => {
                const graphId = getGraphId(template)
                const batchData = graphsData.get(graphId)

                return (
                  <Box
                    key={graphId}
                    gridColumn="1 / -1"
                    minW={0}
                    bg="white"
                    borderRadius="5px"
                    boxShadow="card"
                    p={4}
                    minHeight="100px"
                  >
                    <TimescaleTimeSeriesGraph
                      graph={template as Graph}
                      timeseries={rangeTimescale.timeseries}
                      startDate={startDate}
                      endDate={endDate}
                      chartAxisStartDate={startDate}
                      chartAxisEndDate={endDate}
                      graphBatchData={batchData}
                      batchLoading={loading}
                      availableOverlays={template.availableOverlays}
                      activeOverlays={graphOverlayState.get(graphId) || new Set()}
                      onOverlayToggle={(overlayId, enabled) => {
                        setGraphOverlayState((prev) => {
                          const next = new Map(prev)
                          const current = next.get(graphId) || new Set()
                          if (enabled) {
                            current.add(overlayId)
                          } else {
                            current.delete(overlayId)
                          }
                          next.set(graphId, new Set(current))
                          return next
                        })
                      }}
                      batteryView={template.batteryView}
                      graphsData={graphsData}
                      sharedTimestamps={sharedTimestamps}
                      xAxisTickInterval={sharedXAxisInterval}
                      xAxisTicksMs={sharedXAxisTicksMs}
                      groupedAssetMenu
                    />
                  </Box>
                )
              })
            )}
          </Grid>
        </Flex>
      </Section>
    </SectionRoot>
  )
}
